<template>
<div
    :key='key'
    v-click-outside='vcoConfig'
    class='fw-popup'
    :class='popupClass'
    :style='popupStyles'>
    <div v-if='title || subTitle || showGoBackArrow || $slots["arrowBack"]'
         class='fw-popup__title-wrapper'>
        <div v-if='title'
             class='fw-popup__title'>
            {{ title }}
        </div>
        <div v-if='subTitle'
             class='fw-popup__sub-title h-main-text'>
            {{ subTitle }}
        </div>
    </div>
    <div v-if='showGoBackArrow || $slots["arrowBack"]'
         class='fw-popup__back_arrow'>
        <slot name='arrowBack'>
            <FwIcon
                icon='arrow-left'
                size='20'
                color='#203F6A'
                @click.native='goBack'>
            </FwIcon>
        </slot>
    </div>
    <div class='fw-popup__button--right'>
        <slot name='rightButton'>
        </slot>
    </div>
    <div class='fw-popup__button--left'>
        <slot name='leftButton'>
        </slot>
    </div>
    <span v-if='showClosePopup'
          class='fw-popup__close_popup'>
        <FwIcon
            icon='close'
            size='15'
            :color='iconColor'
            @click.native='emitCloseEvent'>
        </FwIcon>
    </span>

    <div
        v-if='$slots["header"]'>
        <slot name='header'></slot>
    </div>

    <div
        v-if='$slots["body"]'
        class='h-width-100p'
        :style='{"height": heightValue}'>
        <slot name='body'></slot>
    </div>

    <div v-if='$slots["subPopup"]'
         v-show='!hideSubPopup'
         class='fw-popup__sub_popup'>
        <slot name='subPopup'></slot>
    </div>

    <div
        v-if='$slots["footer"]'>
        <slot name='footer'></slot>
    </div>
</div>
</template>

<script lang='ts'>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

const vClickOutside = require('v-click-outside');

@Component({
    directives: {
        clickOutside: vClickOutside.directive,
    },
})
export default class FwPopup extends Vue {
    vcoConfig = {
        handler: this.outsideClickHandler,
    };
    key: number = 1;

    @Prop({ type: Boolean, default: false }) disableClosingOnClickOutside!: boolean;
    @Prop({ type: Boolean, default: false }) allowBackgroundScrolling!: boolean;
    @Prop({ type: Boolean, default: true }) centered!: boolean;
    @Prop({ type: String, default: '100%' }) width!: string;
    @Prop({ type: String, default: '50px 50px 35px' }) padding!: string;
    @Prop({ type: String, default: '' }) maxWidth!: string;
    @Prop({ type: String, default: null }) marginTop!: string;
    @Prop({ type: String, default: '40px' }) popupRadius!: string;
    @Prop({ type: String, default: '#27DBBD' }) iconColor!: string;
    @Prop({ type: String, default: '#ffffff' }) popupBackground!: string;
    @Prop({ type: String, default: null }) title!: string | null;
    @Prop({ type: String, default: null }) subTitle!: string | null;
    @Prop({ type: String, default: 'auto' }) heightValue!: string;
    @Prop({ type: Boolean, default: false }) showGoBackArrow!: boolean;
    @Prop({ type: Boolean, default: false }) showClosePopup!: boolean;
    @Prop({ type: Boolean, default: true }) isFixed!: boolean;
    @Prop({ type: Boolean, default: false }) hideSubPopup!: boolean;

    @Prop({ type: [String, Array, Object], default: null })
    popupClass!: string | Array<string> | { [key: string]: string };
    @Prop({ type: Object, default: () => {} }) propsPopupStyles!: { [key: string]: string };

    get popupStyles() {
        return {
            width: this.width,
            padding: this.padding,
            maxWidth: this.maxWidth,
            marginTop: !this.centered ? this.marginTop : null,
            position: this.isPopupFixed ? 'fixed' : null,
            left: this.isPopupFixed ? '50%' : null,
            transform: this.transformStyle,
            top: this.topStyle,
            borderRadius: this.popupRadius,
            backgroundColor: this.popupBackground,
            ...this.propsPopupStyles,
        };
    }

    get transformStyle() {
        if (!this.marginTop && this.centered) {
            return 'translate(-50%, -50%)';
        }
        return this.isPopupFixed ? 'translateX(-50%)' : null;
    }

    get topStyle() {
        if (this.centered) {
            return '50%';
        }
        return this.isPopupFixed ? '0' : null;
    }

    get isPopupFixed(): boolean {
        return this.isFixed && this.centered;
    }

    emitCloseEvent(): void {
        this.$emit('close');
    }

    outsideClickHandler() {
        if (this.disableClosingOnClickOutside) return;

        this.emitCloseEvent();
    }

    goBack(): void {
        this.$emit('goBack');
    }

    created() {
        if (!this.allowBackgroundScrolling) {
            document.body.style.setProperty('overflow', 'hidden');
        }

        const escapeHandler = (e: any) => {
            // 27 keyCode for Escape button
            if (e.keyCode === 27) {
                this.emitCloseEvent();
            }
        };

        document.addEventListener('keydown', escapeHandler);

        this.$once('hook:destroyed', () => {
            if (!this.allowBackgroundScrolling) {
                document.body.style.removeProperty('overflow');
            }

            document.removeEventListener('keydown', escapeHandler);
        });
    }

    @Watch('hideSubPopup')
    hideSubPopupHandler(value: boolean) {
        if (value) {
            this.key += 1;
        }
    }
}
</script>

<style lang='sass'>
.fw-popup
    box-shadow: 0 15px 56px 0 rgba(0,0,0,0.04)
    display: flex
    align-items: center
    justify-content: center
    padding: 50px 50px 35px
    flex-direction: column
    margin: 0 auto
    @include media('<=phone')
        padding: 20px 25px 20px
        border-radius: 20px

    &__title
        font-size: 22px
        color: $dark-blue
        margin-bottom: 10px
        font-weight: bold
        text-align: center

    &__sub-title
        margin-bottom: 30px
        text-align: center

    &__title-wrapper
        display: flex
        flex-direction: column
        width: 100%
        position: relative

    &__back_arrow
        position: absolute
        top: 40px
        left: 25px
        cursor: pointer
        transition: opacity .3s
        will-change: opacity

        &:hover
            opacity: 0.7
    &__button--right
        position: absolute
        right: 0
        top: 0
    &__button--left
        position: absolute
        left: 0
        top: 0

    &__close_popup
        position: absolute
        right: 40px
        top: 40px
        cursor: pointer
        transition: opacity .3s
        will-change: opacity

        &:hover
            opacity: 0.7

    &__sub_popup
        position: absolute
        border-radius: 20px
        background-color: #fff
        box-shadow: 0 3px 35px 0 rgba(33,63,107,0.05)
        padding: 30px
        z-index: 9999
        width: 350px

        @include media('>tablet')
            right: 0
            transform: translate(50%, 0)
</style>
